import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Tabs,
  Button,
  Space,
  Table,
  Typography,
  Row,
  Col,
  Statistic,
  Tag,
  Modal,
  Form,
  Select,
  DatePicker,
  Input,
  InputNumber,
  message,
  Badge,
  Descriptions,
  Divider,
  Tooltip,
  Spin
} from 'antd';
import {
  DollarOutlined,
  BankOutlined,
  FileTextOutlined,
  CalculatorOutlined,
  DownloadOutlined,
  UploadOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  PrinterOutlined,
  MailOutlined,
  CheckCircleOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  hrLegacyService,
  employeeService,
  salaryStructureService,
  payrollService,
  Employee,
  SalaryStructure as SalaryStructureType,
  PayrollRecord,
  ContributionType,
  CreatePayrollFromEmployeeInput
} from '../../../../services/hrService';

const { Title, Text } = Typography;
const { Option } = Select;

// Using SalaryStructureType from hrService for HK-compatible salary structures
// Local interface for display (backward compatible with old sample data)
interface LegacySalaryStructure {
  id: string;
  name: string;
  baseSalary: number;
  allowances: {
    hra: number;
    transport: number;
    medical: number;
    meal: number;
    bonus: number;
  };
  deductions: {
    pf: number;
    esi: number;
    tax: number;
    insurance: number;
    loan: number;
  };
  isActive: boolean;
  applicableEmployees: string[];
}

interface PayrollEntry {
  id: string;
  employeeId: string;
  employee: {
    firstName: string;
    lastName: string;
    employeeId: string;
    department: string;
    position: string;
  };
  payPeriod: string;
  basicSalary: number;
  allowances: number;
  deductions: number;
  netSalary: number;
  status: 'draft' | 'processed' | 'paid' | 'approved';
  processedDate?: string;
  paidDate?: string;
  paySlipGenerated: boolean;
}

interface TaxSetting {
  id: string;
  name: string;
  type: 'percentage' | 'fixed';
  value: number;
  minSalary: number;
  maxSalary: number;
  isActive: boolean;
}

interface BankAccount {
  id: string;
  employeeId: string;
  accountNumber: string;
  bankName: string;
  ifscCode: string;
  accountType: string;
  isActive: boolean;
}

// Earning item for dynamic payroll entry
interface EarningItem {
  id: string;
  name: string;
  amount: number;
}

// Deduction item for dynamic payroll entry
interface DeductionItem {
  id: string;
  name: string;
  amount: number;
}

// Allowance item for salary structure
interface AllowanceItem {
  id: string;
  name: string;
  amount: number;
}

// Deduction/Tax item for salary structure
interface StructureDeductionItem {
  id: string;
  name: string;
  type: 'percentage' | 'fixed';
  value: number;
}

const PayrollManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState('payroll');
  const [payrollEntries, setPayrollEntries] = useState<PayrollEntry[]>([]);
  const [salaryStructures, setSalaryStructures] = useState<SalaryStructureType[]>([]);
  const [taxSettings, setTaxSettings] = useState<TaxSetting[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [isPayrollModalVisible, setIsPayrollModalVisible] = useState(false);
  const [isStructureModalVisible, setIsStructureModalVisible] = useState(false);
  const [selectedPayroll, setSelectedPayroll] = useState<PayrollEntry | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [editingStructure, setEditingStructure] = useState<SalaryStructureType | null>(null);
  const [selectedEmployeeSalaryStructure, setSelectedEmployeeSalaryStructure] = useState<SalaryStructureType | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);

  // Dynamic earnings and deductions for payroll entry
  const [earningItems, setEarningItems] = useState<EarningItem[]>([
    { id: '1', name: 'Basic Salary', amount: 0 }
  ]);
  const [deductionItems, setDeductionItems] = useState<DeductionItem[]>([]);

  // Dynamic allowances and deductions for salary structure
  const [structureAllowances, setStructureAllowances] = useState<AllowanceItem[]>([
    { id: 'a1', name: '假期補貼', amount: 0 },
    { id: 'a2', name: '超時補貼', amount: 0 },
    { id: 'a3', name: '年假補貼', amount: 0 },
    { id: 'a4', name: '勤工獎金', amount: 0 },
    { id: 'a5', name: '表現獎金', amount: 0 },
    { id: 'a6', name: '其他津貼', amount: 0 }
  ]);
  const [structureDeductions, setStructureDeductions] = useState<StructureDeductionItem[]>([
    { id: 'd1', name: 'Tax', type: 'percentage', value: 0 }
  ]);

  const [form] = Form.useForm();
  const [structureForm] = Form.useForm();

  // Sample data
  const samplePayrollEntries: PayrollEntry[] = [
    {
      id: '1',
      employeeId: 'EMP001',
      employee: {
        firstName: 'John',
        lastName: 'Doe',
        employeeId: 'EMP001',
        department: 'Engineering',
        position: 'Software Engineer'
      },
      payPeriod: '2024-06',
      basicSalary: 85000,
      allowances: 15000,
      deductions: 18000,
      netSalary: 82000,
      status: 'processed',
      processedDate: '2024-06-25',
      paySlipGenerated: true
    },
    {
      id: '2',
      employeeId: 'EMP002',
      employee: {
        firstName: 'Sarah',
        lastName: 'Johnson',
        employeeId: 'EMP002',
        department: 'Marketing',
        position: 'Marketing Manager'
      },
      payPeriod: '2024-06',
      basicSalary: 75000,
      allowances: 12000,
      deductions: 15000,
      netSalary: 72000,
      status: 'approved',
      processedDate: '2024-06-25',
      paySlipGenerated: true
    }
  ];

  const sampleSalaryStructures: SalaryStructure[] = [
    {
      id: '1',
      name: 'Software Engineer - L4',
      baseSalary: 85000,
      allowances: {
        hra: 8500,
        transport: 2000,
        medical: 2500,
        meal: 1500,
        bonus: 500
      },
      deductions: {
        pf: 4250,
        esi: 850,
        tax: 12000,
        insurance: 500,
        loan: 400
      },
      isActive: true,
      applicableEmployees: ['EMP001', 'EMP005']
    },
    {
      id: '2',
      name: 'Marketing Manager - L3',
      baseSalary: 75000,
      allowances: {
        hra: 7500,
        transport: 2000,
        medical: 2000,
        meal: 1000,
        bonus: 500
      },
      deductions: {
        pf: 3750,
        esi: 750,
        tax: 10000,
        insurance: 500,
        loan: 0
      },
      isActive: true,
      applicableEmployees: ['EMP002']
    }
  ];

  const sampleTaxSettings: TaxSetting[] = [
    {
      id: '1',
      name: 'Income Tax (0-50k)',
      type: 'percentage',
      value: 0,
      minSalary: 0,
      maxSalary: 50000,
      isActive: true
    },
    {
      id: '2',
      name: 'Income Tax (50k-100k)',
      type: 'percentage',
      value: 10,
      minSalary: 50000,
      maxSalary: 100000,
      isActive: true
    },
    {
      id: '3',
      name: 'PF Contribution',
      type: 'percentage',
      value: 5,
      minSalary: 0,
      maxSalary: 999999,
      isActive: true
    }
  ];

  // Fetch payroll records from API
  const fetchPayrollRecords = useCallback(async () => {
    setLoading(true);
    try {
      const data = await hrLegacyService.getPayrollRecords();
      // Transform API data to match our interface
      const transformedData = (data || []).map((record: any) => {
        // Handle date format - could be ISO string or Date object
        const payPeriodStart = record.payPeriodStart
          ? (typeof record.payPeriodStart === 'string' ? record.payPeriodStart.substring(0, 7) : dayjs(record.payPeriodStart).format('YYYY-MM'))
          : dayjs().format('YYYY-MM');

        return {
          id: record.id,
          employeeId: record.employeeId,
          employee: record.employee ? {
            firstName: record.employee.firstName || '',
            lastName: record.employee.lastName || '',
            employeeId: record.employee.employeeId || record.employeeId,
            department: record.employee.departmentRef?.name || record.employee.department || 'N/A',
            position: record.employee.positionRef?.title || record.employee.position || 'N/A'
          } : {
            firstName: 'Unknown',
            lastName: '',
            employeeId: record.employeeId,
            department: 'N/A',
            position: 'N/A'
          },
          payPeriod: payPeriodStart,
          basicSalary: Number(record.baseSalary) || 0,
          allowances: Number(record.bonuses) + Number(record.overtime) || 0, // bonuses + overtime = allowances
          deductions: Number(record.deductions) || 0,
          netSalary: Number(record.netPay) || 0, // Backend sends netPay
          // Map backend status to frontend status: PENDING -> draft, PROCESSED -> processed
          status: record.status === 'PENDING' ? 'draft' : (record.status || 'draft').toLowerCase(),
          processedDate: record.processedAt,
          paidDate: record.paidAt,
          paySlipGenerated: record.paySlipGenerated || false
        };
      });
      setPayrollEntries(transformedData);
    } catch (error: any) {
      console.error('Error fetching payroll records:', error);
      // Fall back to sample data if API fails
      setPayrollEntries(samplePayrollEntries);
      message.warning('Using sample data - API connection failed');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch employees for dropdown
  const fetchEmployees = useCallback(async () => {
    try {
      const data = await employeeService.getAll();
      setEmployees(data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  }, []);

  // Fetch salary structures from API
  const fetchSalaryStructures = useCallback(async () => {
    try {
      const data = await salaryStructureService.getAll();
      setSalaryStructures(data || []);
    } catch (error) {
      console.error('Error fetching salary structures:', error);
      // No sample data fallback for salary structures - start with empty
      setSalaryStructures([]);
    }
  }, []);

  useEffect(() => {
    fetchPayrollRecords();
    fetchEmployees();
    fetchSalaryStructures();
    setTaxSettings(sampleTaxSettings);
  }, [fetchPayrollRecords, fetchEmployees, fetchSalaryStructures]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'default';
      case 'processed': return 'orange';
      case 'approved': return 'blue';
      case 'paid': return 'green';
      default: return 'default';
    }
  };

  const handleProcessPayroll = async (recordId: string) => {
    try {
      setLoading(true);
      await hrLegacyService.processPayroll(recordId);
      message.success('Payroll processed successfully');
      fetchPayrollRecords();
    } catch (error: any) {
      message.error(error.response?.data?.error || error.response?.data?.message || 'Failed to process payroll');
    } finally {
      setLoading(false);
    }
  };

  const handleApprovePayroll = async (recordId: string) => {
    try {
      setLoading(true);
      await hrLegacyService.approvePayroll(recordId);
      message.success('Payroll approved successfully');
      fetchPayrollRecords();
    } catch (error: any) {
      message.error(error.response?.data?.error || error.response?.data?.message || 'Failed to approve payroll');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsPaid = async (recordId: string) => {
    try {
      setLoading(true);
      await hrLegacyService.markPayrollAsPaid(recordId);
      message.success('Payroll marked as paid successfully');
      fetchPayrollRecords();
    } catch (error: any) {
      message.error(error.response?.data?.error || error.response?.data?.message || 'Failed to mark payroll as paid');
    } finally {
      setLoading(false);
    }
  };

  const handleBatchProcess = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select payroll entries to process');
      return;
    }

    // Filter to only process draft entries
    const draftEntries = payrollEntries.filter(
      entry => selectedRowKeys.includes(entry.id) && entry.status === 'draft'
    );

    if (draftEntries.length === 0) {
      message.warning('No draft entries selected. Only draft entries can be processed.');
      return;
    }

    try {
      setLoading(true);
      let successCount = 0;
      let failCount = 0;

      for (const entry of draftEntries) {
        try {
          await hrLegacyService.processPayroll(entry.id);
          successCount++;
        } catch {
          failCount++;
        }
      }

      if (successCount > 0) {
        message.success(`Successfully processed ${successCount} payroll entries`);
      }
      if (failCount > 0) {
        message.warning(`Failed to process ${failCount} entries`);
      }

      setSelectedRowKeys([]);
      setCurrentStep(1);
      fetchPayrollRecords();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to process payroll batch');
    } finally {
      setLoading(false);
    }
  };

  const handleBatchApprove = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select payroll entries to approve');
      return;
    }

    // Filter to only approve processed entries
    const processedEntries = payrollEntries.filter(
      entry => selectedRowKeys.includes(entry.id) && entry.status === 'processed'
    );

    if (processedEntries.length === 0) {
      message.warning('No processed entries selected. Only processed entries can be approved.');
      return;
    }

    try {
      setLoading(true);
      let successCount = 0;
      let failCount = 0;

      for (const entry of processedEntries) {
        try {
          await hrLegacyService.approvePayroll(entry.id);
          successCount++;
        } catch {
          failCount++;
        }
      }

      if (successCount > 0) {
        message.success(`Successfully approved ${successCount} payroll entries`);
      }
      if (failCount > 0) {
        message.warning(`Failed to approve ${failCount} entries`);
      }

      setSelectedRowKeys([]);
      setCurrentStep(3);
      fetchPayrollRecords();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to approve payroll batch');
    } finally {
      setLoading(false);
    }
  };

  const handleBatchPay = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select payroll entries to mark as paid');
      return;
    }

    // Filter to only pay approved entries
    const approvedEntries = payrollEntries.filter(
      entry => selectedRowKeys.includes(entry.id) && entry.status === 'approved'
    );

    if (approvedEntries.length === 0) {
      message.warning('No approved entries selected. Only approved entries can be marked as paid.');
      return;
    }

    try {
      setLoading(true);
      let successCount = 0;
      let failCount = 0;

      for (const entry of approvedEntries) {
        try {
          await hrLegacyService.markPayrollAsPaid(entry.id);
          successCount++;
        } catch {
          failCount++;
        }
      }

      if (successCount > 0) {
        message.success(`Successfully marked ${successCount} payroll entries as paid`);
      }
      if (failCount > 0) {
        message.warning(`Failed to mark ${failCount} entries as paid`);
      }

      setSelectedRowKeys([]);
      fetchPayrollRecords();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to mark payroll as paid');
    } finally {
      setLoading(false);
    }
  };

  // Handle employee selection in payroll modal - auto-fetch salary structure
  const handleEmployeeSelect = async (employeeId: string) => {
    const employee = employees.find(emp => emp.id === employeeId);
    setSelectedEmployee(employee || null);

    if (employee?.salaryStructureId) {
      try {
        const structure = await salaryStructureService.getById(employee.salaryStructureId);
        setSelectedEmployeeSalaryStructure(structure);

        // Populate earnings from salary structure
        const newEarnings: EarningItem[] = [
          { id: '1', name: 'Basic Salary', amount: Number(structure.baseSalary) || 0 }
        ];
        if (Number(structure.housingAllowance) > 0) {
          newEarnings.push({ id: '2', name: 'Housing Allowance', amount: Number(structure.housingAllowance) });
        }
        if (Number(structure.transportAllowance) > 0) {
          newEarnings.push({ id: '3', name: 'Transport Allowance', amount: Number(structure.transportAllowance) });
        }
        if (Number(structure.mealAllowance) > 0) {
          newEarnings.push({ id: '4', name: 'Meal Allowance', amount: Number(structure.mealAllowance) });
        }
        if (Number(structure.phoneAllowance) > 0) {
          newEarnings.push({ id: '5', name: 'Phone Allowance', amount: Number(structure.phoneAllowance) });
        }
        // Add other allowances from JSON field (Chinese-named allowances like 假期補貼, 超時補貼, etc.)
        if (structure.otherAllowances && Array.isArray(structure.otherAllowances)) {
          (structure.otherAllowances as any[]).forEach((item, idx) => {
            if (Number(item.amount) > 0) {
              newEarnings.push({ id: `o${idx}`, name: item.name, amount: Number(item.amount) });
            }
          });
        }
        setEarningItems(newEarnings);

        // Calculate deductions from salary structure
        const baseSalary = Number(structure.baseSalary) || 0;
        let totalAllowances = (Number(structure.housingAllowance) || 0) +
                               (Number(structure.transportAllowance) || 0) +
                               (Number(structure.mealAllowance) || 0) +
                               (Number(structure.phoneAllowance) || 0);
        // Include other allowances in gross pay calculation
        if (structure.otherAllowances && Array.isArray(structure.otherAllowances)) {
          totalAllowances += (structure.otherAllowances as any[]).reduce((sum, item) => sum + Number(item.amount || 0), 0);
        }
        const grossPay = baseSalary + totalAllowances;
        const taxRate = Number(structure.taxRate) || 0;
        const employeeContribRate = Number(structure.employeeContribRate) || 0;

        const newDeductions: DeductionItem[] = [];
        // Add MPF/CPF contribution if applicable
        if (employeeContribRate > 0) {
          const relevantIncome = Math.min(grossPay, Number(structure.contribMaxIncome) || grossPay);
          let contribution = relevantIncome * (employeeContribRate / 100);
          if (structure.contribCapped && Number(structure.contribCapAmount) > 0) {
            contribution = Math.min(contribution, Number(structure.contribCapAmount));
          }
          newDeductions.push({
            id: 'd1',
            name: `${structure.contribType || 'MPF'} Contribution`,
            amount: Math.round(contribution * 100) / 100
          });
        }
        // Add tax deduction if applicable
        if (taxRate > 0) {
          const taxAmount = Math.round((grossPay * (taxRate / 100)) * 100) / 100;
          newDeductions.push({ id: 'd2', name: structure.taxName || 'Tax', amount: taxAmount });
        }
        // Add other deductions from JSON field (rate-based deductions)
        if (structure.otherDeductionRates && Array.isArray(structure.otherDeductionRates)) {
          (structure.otherDeductionRates as any[]).forEach((item, idx) => {
            if (Number(item.rate) > 0) {
              const deductionAmount = Math.round((grossPay * (Number(item.rate) / 100)) * 100) / 100;
              newDeductions.push({ id: `od${idx}`, name: item.name, amount: deductionAmount });
            }
          });
        }
        setDeductionItems(newDeductions);

        message.info(`Salary structure "${structure.name}" auto-loaded for this employee`);
      } catch (error) {
        console.error('Failed to fetch salary structure:', error);
        setSelectedEmployeeSalaryStructure(null);
        setEarningItems([{ id: '1', name: 'Basic Salary', amount: 0 }]);
        setDeductionItems([]);
      }
    } else {
      setSelectedEmployeeSalaryStructure(null);
      // Reset to default values if no salary structure
      setEarningItems([{ id: '1', name: 'Basic Salary', amount: employee?.salary || 0 }]);
      setDeductionItems([]);
    }
  };

  // Calculate totals for earnings and deductions
  const totalEarnings = earningItems.reduce((sum, item) => sum + (item.amount || 0), 0);
  const totalDeductions = deductionItems.reduce((sum, item) => sum + (item.amount || 0), 0);
  const netSalary = totalEarnings - totalDeductions;

  // Add new earning item
  const addEarningItem = () => {
    const newId = `e${Date.now()}`;
    setEarningItems([...earningItems, { id: newId, name: '', amount: 0 }]);
  };

  // Remove earning item
  const removeEarningItem = (id: string) => {
    if (earningItems.length > 1) {
      setEarningItems(earningItems.filter(item => item.id !== id));
    }
  };

  // Update earning item
  const updateEarningItem = (id: string, field: 'name' | 'amount', value: string | number) => {
    setEarningItems(earningItems.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  // Add new deduction item
  const addDeductionItem = () => {
    const newId = `d${Date.now()}`;
    setDeductionItems([...deductionItems, { id: newId, name: '', amount: 0 }]);
  };

  // Remove deduction item
  const removeDeductionItem = (id: string) => {
    setDeductionItems(deductionItems.filter(item => item.id !== id));
  };

  // Update deduction item
  const updateDeductionItem = (id: string, field: 'name' | 'amount', value: string | number) => {
    setDeductionItems(deductionItems.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  // ============ Salary Structure Allowances/Deductions Helpers ============

  // Calculate totals for salary structure
  const structureTotalAllowances = structureAllowances.reduce((sum, item) => sum + (item.amount || 0), 0);

  // Add new allowance item to salary structure
  const addStructureAllowance = () => {
    const newId = `a${Date.now()}`;
    setStructureAllowances([...structureAllowances, { id: newId, name: '', amount: 0 }]);
  };

  // Remove allowance item from salary structure
  const removeStructureAllowance = (id: string) => {
    setStructureAllowances(structureAllowances.filter(item => item.id !== id));
  };

  // Update allowance item in salary structure
  const updateStructureAllowance = (id: string, field: 'name' | 'amount', value: string | number) => {
    setStructureAllowances(structureAllowances.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  // Add new deduction item to salary structure
  const addStructureDeduction = () => {
    const newId = `d${Date.now()}`;
    setStructureDeductions([...structureDeductions, { id: newId, name: '', type: 'percentage', value: 0 }]);
  };

  // Remove deduction item from salary structure
  const removeStructureDeduction = (id: string) => {
    setStructureDeductions(structureDeductions.filter(item => item.id !== id));
  };

  // Update deduction item in salary structure
  const updateStructureDeduction = (id: string, field: 'name' | 'type' | 'value', value: string | number) => {
    setStructureDeductions(structureDeductions.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  // Reset salary structure form with allowances/deductions
  const resetStructureForm = () => {
    structureForm.resetFields();
    setStructureAllowances([
      { id: 'a1', name: '假期補貼', amount: 0 },
      { id: 'a2', name: '超時補貼', amount: 0 },
      { id: 'a3', name: '年假補貼', amount: 0 },
      { id: 'a4', name: '勤工獎金', amount: 0 },
      { id: 'a5', name: '表現獎金', amount: 0 },
      { id: 'a6', name: '其他津貼', amount: 0 }
    ]);
    setStructureDeductions([
      { id: 'd1', name: 'Tax', type: 'percentage', value: 0 }
    ]);
    setEditingStructure(null);
  };

  // Load existing salary structure into form
  const loadStructureForEditing = (structure: SalaryStructureType) => {
    setEditingStructure(structure);
    structureForm.setFieldsValue({
      ...structure,
      baseSalary: Number(structure.baseSalary),
      employeeContribRate: Number(structure.employeeContribRate),
      employerContribRate: Number(structure.employerContribRate),
      contribCapAmount: Number(structure.contribCapAmount),
      contribMinIncome: Number(structure.contribMinIncome),
      contribMaxIncome: Number(structure.contribMaxIncome),
    });

    // Load allowances
    const loadedAllowances: AllowanceItem[] = [];
    if (Number(structure.housingAllowance) > 0) {
      loadedAllowances.push({ id: 'a1', name: 'Housing Allowance', amount: Number(structure.housingAllowance) });
    }
    if (Number(structure.transportAllowance) > 0) {
      loadedAllowances.push({ id: 'a2', name: 'Transport Allowance', amount: Number(structure.transportAllowance) });
    }
    if (Number(structure.mealAllowance) > 0) {
      loadedAllowances.push({ id: 'a3', name: 'Meal Allowance', amount: Number(structure.mealAllowance) });
    }
    if (Number(structure.phoneAllowance) > 0) {
      loadedAllowances.push({ id: 'a4', name: 'Phone Allowance', amount: Number(structure.phoneAllowance) });
    }
    // Add any other allowances from otherAllowances JSON if present
    if (structure.otherAllowances && Array.isArray(structure.otherAllowances)) {
      (structure.otherAllowances as any[]).forEach((item, idx) => {
        loadedAllowances.push({ id: `ao${idx}`, name: item.name, amount: Number(item.amount) });
      });
    }
    if (loadedAllowances.length === 0) {
      loadedAllowances.push({ id: 'a1', name: '其他津貼', amount: 0 });
    }
    setStructureAllowances(loadedAllowances);

    // Load deductions/tax
    const loadedDeductions: StructureDeductionItem[] = [];
    if (Number(structure.taxRate) > 0) {
      loadedDeductions.push({
        id: 'd1',
        name: structure.taxName || 'Tax',
        type: 'percentage',
        value: Number(structure.taxRate)
      });
    }
    // Add any other deductions from otherDeductionRates JSON if present
    if (structure.otherDeductionRates && Array.isArray(structure.otherDeductionRates)) {
      (structure.otherDeductionRates as any[]).forEach((item, idx) => {
        loadedDeductions.push({
          id: `do${idx}`,
          name: item.name,
          type: item.type || 'percentage',
          value: Number(item.value)
        });
      });
    }
    if (loadedDeductions.length === 0) {
      loadedDeductions.push({ id: 'd1', name: 'Tax', type: 'percentage', value: 0 });
    }
    setStructureDeductions(loadedDeductions);

    setIsStructureModalVisible(true);
  };

  const handleCreatePayroll = async (values: any) => {
    try {
      setLoading(true);

      // Calculate totals from earnings and deductions tables
      const calculatedTotalEarnings = earningItems.reduce((sum, item) => sum + (item.amount || 0), 0);
      const calculatedTotalDeductions = deductionItems.reduce((sum, item) => sum + (item.amount || 0), 0);

      // Get basic salary from earnings (first item or find by name)
      const basicSalaryItem = earningItems.find(item => item.name === 'Basic Salary');
      const baseSalary = basicSalaryItem?.amount || earningItems[0]?.amount || 0;

      // Calculate allowances (all earnings except basic salary)
      const allowances = calculatedTotalEarnings - baseSalary;

      // If employee has a salary structure, use the new API that auto-fetches from structure
      if (selectedEmployeeSalaryStructure && selectedEmployee?.salaryStructureId) {
        const data: CreatePayrollFromEmployeeInput = {
          employeeId: values.employeeId,
          payPeriodStart: values.payPeriod.startOf('month').format('YYYY-MM-DD'),
          payPeriodEnd: values.payPeriod.endOf('month').format('YYYY-MM-DD'),
          overtimeHours: 0,
          bonuses: allowances, // Additional allowances as bonuses
          notes: values.notes
        };
        await payrollService.createFromEmployee(data);
        message.success('Payroll entry created from salary structure successfully');
      } else {
        // Fallback to manual entry if no salary structure
        const data = {
          employeeId: values.employeeId,
          payPeriodStart: values.payPeriod.startOf('month').format('YYYY-MM-DD'),
          payPeriodEnd: values.payPeriod.endOf('month').format('YYYY-MM-DD'),
          baseSalary: Math.max(baseSalary, 1),
          overtime: 0,
          bonuses: allowances,
          deductions: calculatedTotalDeductions
        };
        await hrLegacyService.createPayrollRecord(data);
        message.success('Payroll entry created successfully');
      }

      setIsPayrollModalVisible(false);
      setSelectedEmployee(null);
      setSelectedEmployeeSalaryStructure(null);
      setEarningItems([{ id: '1', name: 'Basic Salary', amount: 0 }]);
      setDeductionItems([]);
      form.resetFields();
      fetchPayrollRecords();
    } catch (error: any) {
      message.error(error.response?.data?.message || error.message || 'Failed to create payroll entry');
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePaySlip = async (entryId: string) => {
    try {
      setLoading(true);
      // Pay slip generation would be implemented here
      message.success('Pay slip generated successfully');
    } catch (error) {
      message.error('Failed to generate pay slip');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkPayment = async (selectedEntries: string[]) => {
    try {
      setLoading(true);
      // Bulk payment would be implemented here
      message.success('Bulk payment initiated successfully');
    } catch (error) {
      message.error('Failed to initiate bulk payment');
    } finally {
      setLoading(false);
    }
  };

  // Salary Structure CRUD handlers
  const handleSaveSalaryStructure = async (values: any) => {
    try {
      setLoading(true);

      // Convert dynamic allowances to API format
      let housingAllowance = 0;
      let transportAllowance = 0;
      let mealAllowance = 0;
      let phoneAllowance = 0;
      const otherAllowances: { name: string; amount: number }[] = [];

      structureAllowances.forEach(item => {
        const lowerName = item.name.toLowerCase();
        if (lowerName.includes('housing')) {
          housingAllowance = item.amount;
        } else if (lowerName.includes('transport')) {
          transportAllowance = item.amount;
        } else if (lowerName.includes('meal')) {
          mealAllowance = item.amount;
        } else if (lowerName.includes('phone')) {
          phoneAllowance = item.amount;
        } else if (item.name.trim()) {
          otherAllowances.push({ name: item.name, amount: item.amount });
        }
      });

      // Convert dynamic deductions to API format
      let taxName = '';
      let taxRate = 0;
      const otherDeductionRates: { name: string; type: string; value: number }[] = [];

      structureDeductions.forEach((item, index) => {
        // First percentage-type deduction with 'tax' in name becomes the main tax
        if (index === 0 || (item.type === 'percentage' && item.name.toLowerCase().includes('tax') && !taxName)) {
          taxName = item.name;
          taxRate = item.value;
        } else if (item.name.trim()) {
          otherDeductionRates.push({ name: item.name, type: item.type, value: item.value });
        }
      });

      const dataToSave = {
        ...values,
        housingAllowance,
        transportAllowance,
        mealAllowance,
        phoneAllowance,
        otherAllowances: otherAllowances.length > 0 ? otherAllowances : null,
        taxName,
        taxRate,
        otherDeductionRates: otherDeductionRates.length > 0 ? otherDeductionRates : null
      };

      if (editingStructure) {
        // Update existing structure
        await salaryStructureService.update(editingStructure.id, dataToSave);
        message.success('Salary structure updated successfully');
      } else {
        // Create new structure
        await salaryStructureService.create(dataToSave);
        message.success('Salary structure created successfully');
      }
      setIsStructureModalVisible(false);
      resetStructureForm();
      fetchSalaryStructures();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to save salary structure');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSalaryStructure = async (id: string) => {
    Modal.confirm({
      title: 'Delete Salary Structure',
      content: 'Are you sure you want to delete this salary structure?',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          setLoading(true);
          await salaryStructureService.delete(id);
          message.success('Salary structure deleted successfully');
          fetchSalaryStructures();
        } catch (error: any) {
          message.error(error.response?.data?.error || 'Failed to delete salary structure');
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const payrollColumns: ColumnsType<PayrollEntry> = [
    {
      title: 'Employee',
      key: 'employee',
      render: (_, record) => (
        <Space>
          <div>
            <div>{`${record.employee.firstName} ${record.employee.lastName}`}</div>
            <Text type="secondary" style={{ fontSize: 12 }}>{record.employee.employeeId}</Text>
          </div>
        </Space>
      )
    },
    {
      title: 'Department',
      dataIndex: ['employee', 'department'],
      key: 'department'
    },
    {
      title: 'Pay Period',
      dataIndex: 'payPeriod',
      key: 'payPeriod',
      render: (period) => dayjs(period).format('MMM YYYY')
    },
    {
      title: 'Basic Salary',
      dataIndex: 'basicSalary',
      key: 'basicSalary',
      render: (amount) => `$${amount.toLocaleString()}`
    },
    {
      title: 'Allowances',
      dataIndex: 'allowances',
      key: 'allowances',
      render: (amount) => `$${amount.toLocaleString()}`
    },
    {
      title: 'Deductions',
      dataIndex: 'deductions',
      key: 'deductions',
      render: (amount) => `$${amount.toLocaleString()}`
    },
    {
      title: 'Net Salary',
      dataIndex: 'netSalary',
      key: 'netSalary',
      render: (amount) => <Text strong>${amount.toLocaleString()}</Text>
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <Tag color={getStatusColor(status)}>{status.toUpperCase()}</Tag>
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space wrap size="small">
          <Button
            type="link"
            size="small"
            icon={<FileTextOutlined />}
            onClick={() => setSelectedPayroll(record)}
          >
            View
          </Button>
          {record.status === 'draft' && (
            <Button
              type="link"
              size="small"
              icon={<CalculatorOutlined />}
              onClick={() => handleProcessPayroll(record.id)}
              style={{ color: '#1890ff' }}
            >
              Process
            </Button>
          )}
          {record.status === 'processed' && (
            <Button
              type="link"
              size="small"
              icon={<CheckCircleOutlined />}
              onClick={() => handleApprovePayroll(record.id)}
              style={{ color: '#52c41a' }}
            >
              Approve
            </Button>
          )}
          {record.status === 'approved' && (
            <Button
              type="link"
              size="small"
              icon={<DollarOutlined />}
              onClick={() => handleMarkAsPaid(record.id)}
              style={{ color: '#722ed1' }}
            >
              Pay
            </Button>
          )}
          <Button
            type="link"
            size="small"
            icon={<PrinterOutlined />}
            onClick={() => handleGeneratePaySlip(record.id)}
            disabled={record.status === 'draft'}
          >
            Pay Slip
          </Button>
          <Button
            type="link"
            size="small"
            icon={<MailOutlined />}
            disabled={record.status === 'draft'}
          >
            Email
          </Button>
        </Space>
      )
    }
  ];

  const structureColumns: ColumnsType<SalaryStructureType> = [
    {
      title: 'Structure Name',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <div>
          <div>{name}</div>
          {record.code && <Text type="secondary" style={{ fontSize: 12 }}>{record.code}</Text>}
        </div>
      )
    },
    {
      title: 'Base Salary',
      dataIndex: 'baseSalary',
      key: 'baseSalary',
      render: (amount, record) => `${record.currency} ${Number(amount).toLocaleString()}`
    },
    {
      title: 'Total Allowances',
      key: 'totalAllowances',
      render: (_, record) => {
        let total = Number(record.housingAllowance || 0) +
                    Number(record.transportAllowance || 0) +
                    Number(record.mealAllowance || 0) +
                    Number(record.phoneAllowance || 0);
        // Include otherAllowances from JSON field
        if (record.otherAllowances && Array.isArray(record.otherAllowances)) {
          total += (record.otherAllowances as any[]).reduce((sum, item) => sum + Number(item.amount || 0), 0);
        }
        return `${record.currency} ${total.toLocaleString()}`;
      }
    },
    {
      title: 'Contribution',
      key: 'contribution',
      render: (_, record) => (
        <Tooltip title={`Employee: ${record.employeeContribRate}% | Employer: ${record.employerContribRate}%`}>
          <Tag color="blue">{record.contribType}</Tag>
        </Tooltip>
      )
    },
    {
      title: 'Currency',
      dataIndex: 'currency',
      key: 'currency',
      render: (currency) => <Tag>{currency}</Tag>
    },
    {
      title: 'Employees',
      key: 'employees',
      render: (_, record) => {
        const count = record._count?.employees || 0;
        return (
          <Badge count={count} showZero style={{ backgroundColor: count > 0 ? '#52c41a' : '#d9d9d9' }}>
            <Tag style={{ marginRight: 0 }}>{count} assigned</Tag>
          </Badge>
        );
      }
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => loadStructureForEditing(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="text"
              icon={<DeleteOutlined />}
              danger
              onClick={() => handleDeleteSalaryStructure(record.id)}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  const renderPayrollSummary = () => {
    const currentMonth = payrollEntries.filter(entry => 
      dayjs(entry.payPeriod).month() === dayjs().month()
    );
    const totalGrossPay = currentMonth.reduce((sum, entry) => sum + entry.basicSalary + entry.allowances, 0);
    const totalDeductions = currentMonth.reduce((sum, entry) => sum + entry.deductions, 0);
    const totalNetPay = currentMonth.reduce((sum, entry) => sum + entry.netSalary, 0);
    const processedCount = currentMonth.filter(entry => entry.status === 'processed').length;

    return (
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Gross Pay"
              value={totalGrossPay}
              prefix={<DollarOutlined />}
              formatter={value => `$${value?.toLocaleString()}`}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Deductions"
              value={totalDeductions}
              prefix={<CalculatorOutlined />}
              formatter={value => `$${value?.toLocaleString()}`}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Net Pay"
              value={totalNetPay}
              prefix={<BankOutlined />}
              formatter={value => `$${value?.toLocaleString()}`}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Processed Entries"
              value={processedCount}
              prefix={<CheckCircleOutlined />}
              suffix={`/ ${currentMonth.length}`}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>
    );
  };

  return (
    <div style={{ padding: '24px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Title level={2}>Payroll Management</Title>
        <Space>
          <Button icon={<UploadOutlined />}>Import Data</Button>
          <Button icon={<DownloadOutlined />}>Export Report</Button>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={() => setIsPayrollModalVisible(true)}
          >
            New Payroll Entry
          </Button>
        </Space>
      </Row>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <Tabs.TabPane tab="Payroll Processing" key="payroll">
          {renderPayrollSummary()}

          <Card
            title="Current Payroll Entries"
            extra={
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchPayrollRecords}
                loading={loading}
              >
                Refresh
              </Button>
            }
          >
            {/* Bulk Action Buttons */}
            <div style={{ marginBottom: 16 }}>
              <Space wrap>
                <Button
                  type="primary"
                  icon={<CalculatorOutlined />}
                  onClick={handleBatchProcess}
                  loading={loading}
                  disabled={selectedRowKeys.length === 0 || !payrollEntries.some(e => selectedRowKeys.includes(e.id) && e.status === 'draft')}
                >
                  Process Draft ({payrollEntries.filter(e => selectedRowKeys.includes(e.id) && e.status === 'draft').length})
                </Button>
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  onClick={handleBatchApprove}
                  loading={loading}
                  disabled={selectedRowKeys.length === 0 || !payrollEntries.some(e => selectedRowKeys.includes(e.id) && e.status === 'processed')}
                  style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                >
                  Approve Processed ({payrollEntries.filter(e => selectedRowKeys.includes(e.id) && e.status === 'processed').length})
                </Button>
                <Button
                  type="primary"
                  icon={<DollarOutlined />}
                  onClick={handleBatchPay}
                  loading={loading}
                  disabled={selectedRowKeys.length === 0 || !payrollEntries.some(e => selectedRowKeys.includes(e.id) && e.status === 'approved')}
                  style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}
                >
                  Mark as Paid ({payrollEntries.filter(e => selectedRowKeys.includes(e.id) && e.status === 'approved').length})
                </Button>
              </Space>
            </div>

            <Spin spinning={loading}>
            <Table
              columns={payrollColumns}
              dataSource={payrollEntries}
              rowKey="id"
              pagination={{ pageSize: 10 }}
              scroll={{ x: 'max-content' }}
              rowSelection={{
                type: 'checkbox',
                selectedRowKeys,
                onChange: (keys) => {
                  setSelectedRowKeys(keys);
                },
                getCheckboxProps: (record) => ({
                  disabled: record.status === 'paid', // Can't select already paid entries
                })
              }}
            />
            </Spin>
          </Card>
        </Tabs.TabPane>

        <Tabs.TabPane tab="Salary Structures" key="structures">
          <Card
            title="Salary Structures"
            extra={
              <Space>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={fetchSalaryStructures}
                  loading={loading}
                >
                  Refresh
                </Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    resetStructureForm();
                    setIsStructureModalVisible(true);
                  }}
                >
                  Add Structure
                </Button>
              </Space>
            }
          >
            <Spin spinning={loading}>
              <Table
                columns={structureColumns}
                dataSource={salaryStructures}
                rowKey="id"
                pagination={{ pageSize: 10 }}
                locale={{ emptyText: 'No salary structures found. Click "Add Structure" to create one.' }}
              />
            </Spin>
          </Card>
        </Tabs.TabPane>

        <Tabs.TabPane tab="Tax Settings" key="tax-settings">
          <Card title="Tax Configuration">
            <Row gutter={[16, 16]}>
              {taxSettings.map(setting => (
                <Col xs={24} md={12} lg={8} key={setting.id}>
                  <Card size="small" title={setting.name}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <div>Type: {setting.type}</div>
                      <div>Rate: {setting.value}%</div>
                      <div>Range: ${setting.minSalary.toLocaleString()} - ${setting.maxSalary.toLocaleString()}</div>
                      <Tag color={setting.isActive ? 'green' : 'red'}>
                        {setting.isActive ? 'Active' : 'Inactive'}
                      </Tag>
                    </Space>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>
        </Tabs.TabPane>

        <Tabs.TabPane tab="Reports" key="reports">
          <Card title="Payroll Reports">
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8}>
                <Card size="small">
                  <div style={{ textAlign: 'center' }}>
                    <FileTextOutlined style={{ fontSize: 24, marginBottom: 8 }} />
                    <div>Monthly Payroll Report</div>
                    <Button type="link" size="small">Download</Button>
                  </div>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Card size="small">
                  <div style={{ textAlign: 'center' }}>
                    <CalculatorOutlined style={{ fontSize: 24, marginBottom: 8 }} />
                    <div>Tax Summary Report</div>
                    <Button type="link" size="small">Download</Button>
                  </div>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Card size="small">
                  <div style={{ textAlign: 'center' }}>
                    <BankOutlined style={{ fontSize: 24, marginBottom: 8 }} />
                    <div>Bank Transfer Report</div>
                    <Button type="link" size="small">Download</Button>
                  </div>
                </Card>
              </Col>
            </Row>
          </Card>
        </Tabs.TabPane>
      </Tabs>

      {/* Payroll Details Modal */}
      <Modal
        title="Payroll Entry Details"
        open={!!selectedPayroll}
        onCancel={() => setSelectedPayroll(null)}
        footer={[
          <Button key="close" onClick={() => setSelectedPayroll(null)}>
            Close
          </Button>,
          <Button key="print" icon={<PrinterOutlined />}>
            Print Pay Slip
          </Button>,
          <Button key="email" type="primary" icon={<MailOutlined />}>
            Email Pay Slip
          </Button>
        ]}
        width={700}
      >
        {selectedPayroll && (
          <div>
            <Descriptions title="Employee Information" bordered size="small">
              <Descriptions.Item label="Name">
                {`${selectedPayroll.employee.firstName} ${selectedPayroll.employee.lastName}`}
              </Descriptions.Item>
              <Descriptions.Item label="Employee ID">
                {selectedPayroll.employee.employeeId}
              </Descriptions.Item>
              <Descriptions.Item label="Department">
                {selectedPayroll.employee.department}
              </Descriptions.Item>
              <Descriptions.Item label="Position">
                {selectedPayroll.employee.position}
              </Descriptions.Item>
              <Descriptions.Item label="Pay Period">
                {dayjs(selectedPayroll.payPeriod).format('MMMM YYYY')}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={getStatusColor(selectedPayroll.status)}>
                  {selectedPayroll.status.toUpperCase()}
                </Tag>
              </Descriptions.Item>
            </Descriptions>

            <Divider />

            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Card title="Earnings" size="small">
                  <div style={{ marginBottom: 8 }}>
                    Basic Salary: ${selectedPayroll.basicSalary.toLocaleString()}
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    Allowances: ${selectedPayroll.allowances.toLocaleString()}
                  </div>
                  <Divider />
                  <div>
                    <strong>Total Earnings: ${(selectedPayroll.basicSalary + selectedPayroll.allowances).toLocaleString()}</strong>
                  </div>
                </Card>
              </Col>
              <Col span={12}>
                <Card title="Deductions" size="small">
                  <div style={{ marginBottom: 8 }}>
                    Total Deductions: ${selectedPayroll.deductions.toLocaleString()}
                  </div>
                  <Divider />
                  <div>
                    <strong>Net Salary: ${selectedPayroll.netSalary.toLocaleString()}</strong>
                  </div>
                </Card>
              </Col>
            </Row>
          </div>
        )}
      </Modal>

      {/* Add Payroll Entry Modal */}
      <Modal
        title="Add Payroll Entry"
        open={isPayrollModalVisible}
        onCancel={() => {
          setIsPayrollModalVisible(false);
          setSelectedEmployee(null);
          setSelectedEmployeeSalaryStructure(null);
          setEarningItems([{ id: '1', name: 'Basic Salary', amount: 0 }]);
          setDeductionItems([]);
          form.resetFields();
        }}
        footer={null}
        width={900}
      >
        <Form form={form} layout="vertical" onFinish={handleCreatePayroll}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="employeeId" label="Employee" rules={[{ required: true }]}>
                <Select
                  placeholder="Select employee"
                  showSearch
                  optionFilterProp="children"
                  onChange={handleEmployeeSelect}
                  filterOption={(input, option) =>
                    (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {employees.map(emp => (
                    <Option key={emp.id} value={emp.id}>
                      {`${emp.firstName} ${emp.lastName} (${emp.employeeId})${emp.salaryStructureId ? ' ✓' : ''}`}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="payPeriod" label="Pay Period" rules={[{ required: true }]}>
                <DatePicker picker="month" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          {/* Show salary structure info if employee has one assigned */}
          {selectedEmployeeSalaryStructure && (
            <Card size="small" style={{ marginBottom: 16, backgroundColor: '#f6ffed', border: '1px solid #b7eb8f' }}>
              <Row gutter={[8, 4]}>
                <Col span={24}>
                  <Tag color="green">Using Salary Structure: {selectedEmployeeSalaryStructure.name}</Tag>
                </Col>
                <Col span={8}>
                  <Text type="secondary">Base: </Text>
                  <Text strong>${Number(selectedEmployeeSalaryStructure.baseSalary).toLocaleString()}</Text>
                </Col>
                <Col span={8}>
                  <Text type="secondary">{selectedEmployeeSalaryStructure.contribType}: </Text>
                  <Text strong>{selectedEmployeeSalaryStructure.employeeContribRate}%/{selectedEmployeeSalaryStructure.employerContribRate}%</Text>
                </Col>
                <Col span={8}>
                  <Text type="secondary">Currency: </Text>
                  <Text strong>{selectedEmployeeSalaryStructure.currency}</Text>
                </Col>
              </Row>
            </Card>
          )}

          {!selectedEmployeeSalaryStructure && selectedEmployee && (
            <Card size="small" style={{ marginBottom: 16, backgroundColor: '#fffbe6', border: '1px solid #ffe58f' }}>
              <Text type="warning">
                This employee has no salary structure assigned. Please enter values manually or assign a salary structure first.
              </Text>
            </Card>
          )}

          {/* Earnings and Deductions Tables */}
          <Row gutter={16}>
            {/* Earnings Table */}
            <Col span={12}>
              <Card
                size="small"
                title={
                  <Space>
                    <DollarOutlined style={{ color: '#52c41a' }} />
                    <span>Earnings</span>
                  </Space>
                }
                extra={
                  <Button
                    type="link"
                    icon={<PlusOutlined />}
                    onClick={addEarningItem}
                    size="small"
                  >
                    Add
                  </Button>
                }
                style={{ marginBottom: 16 }}
              >
                <div style={{ maxHeight: 250, overflowY: 'auto' }}>
                  {earningItems.map((item, index) => (
                    <div key={item.id} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                      <Input
                        placeholder="Item name"
                        value={item.name}
                        onChange={(e) => updateEarningItem(item.id, 'name', e.target.value)}
                        style={{ flex: 1 }}
                        disabled={index === 0 && item.name === 'Basic Salary'}
                      />
                      <InputNumber
                        placeholder="Amount"
                        value={item.amount}
                        onChange={(val) => updateEarningItem(item.id, 'amount', val || 0)}
                        style={{ width: 120 }}
                        min={0}
                        formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        parser={value => value!.replace(/\$\s?|(,*)/g, '')}
                      />
                      <Button
                        type="text"
                        icon={<DeleteOutlined />}
                        danger
                        onClick={() => removeEarningItem(item.id)}
                        disabled={earningItems.length === 1}
                        size="small"
                      />
                    </div>
                  ))}
                </div>
                <Divider style={{ margin: '12px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
                  <Text strong style={{ color: '#52c41a' }}>Total Earnings:</Text>
                  <Text strong style={{ color: '#52c41a' }}>${totalEarnings.toLocaleString()}</Text>
                </div>
              </Card>
            </Col>

            {/* Deductions Table */}
            <Col span={12}>
              <Card
                size="small"
                title={
                  <Space>
                    <CalculatorOutlined style={{ color: '#ff4d4f' }} />
                    <span>Deductions</span>
                  </Space>
                }
                extra={
                  <Button
                    type="link"
                    icon={<PlusOutlined />}
                    onClick={addDeductionItem}
                    size="small"
                  >
                    Add
                  </Button>
                }
                style={{ marginBottom: 16 }}
              >
                <div style={{ maxHeight: 250, overflowY: 'auto' }}>
                  {deductionItems.length === 0 ? (
                    <div style={{ textAlign: 'center', color: '#999', padding: '20px 0' }}>
                      <Text type="secondary">No deductions added</Text>
                    </div>
                  ) : (
                    deductionItems.map((item) => (
                      <div key={item.id} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                        <Input
                          placeholder="Item name"
                          value={item.name}
                          onChange={(e) => updateDeductionItem(item.id, 'name', e.target.value)}
                          style={{ flex: 1 }}
                        />
                        <InputNumber
                          placeholder="Amount"
                          value={item.amount}
                          onChange={(val) => updateDeductionItem(item.id, 'amount', val || 0)}
                          style={{ width: 120 }}
                          min={0}
                          formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                          parser={value => value!.replace(/\$\s?|(,*)/g, '')}
                        />
                        <Button
                          type="text"
                          icon={<DeleteOutlined />}
                          danger
                          onClick={() => removeDeductionItem(item.id)}
                          size="small"
                        />
                      </div>
                    ))
                  )}
                </div>
                <Divider style={{ margin: '12px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
                  <Text strong style={{ color: '#ff4d4f' }}>Total Deductions:</Text>
                  <Text strong style={{ color: '#ff4d4f' }}>${totalDeductions.toLocaleString()}</Text>
                </div>
              </Card>
            </Col>
          </Row>

          {/* Net Salary Summary */}
          <Card
            size="small"
            style={{
              marginBottom: 16,
              backgroundColor: '#f0f5ff',
              border: '1px solid #adc6ff'
            }}
          >
            <Row gutter={16} align="middle">
              <Col span={8}>
                <Statistic
                  title="Total Earnings"
                  value={totalEarnings}
                  prefix={<DollarOutlined />}
                  valueStyle={{ color: '#52c41a', fontSize: 18 }}
                  formatter={value => `$${value?.toLocaleString()}`}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Total Deductions"
                  value={totalDeductions}
                  prefix={<CalculatorOutlined />}
                  valueStyle={{ color: '#ff4d4f', fontSize: 18 }}
                  formatter={value => `$${value?.toLocaleString()}`}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Net Salary"
                  value={netSalary}
                  prefix={<BankOutlined />}
                  valueStyle={{ color: '#1890ff', fontSize: 22, fontWeight: 'bold' }}
                  formatter={value => `$${value?.toLocaleString()}`}
                />
              </Col>
            </Row>
          </Card>

          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} placeholder="Optional notes" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                Create Payroll Entry
              </Button>
              <Button onClick={() => {
                setIsPayrollModalVisible(false);
                setEarningItems([{ id: '1', name: 'Basic Salary', amount: 0 }]);
                setDeductionItems([]);
                form.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Salary Structure Modal */}
      <Modal
        title={editingStructure ? 'Edit Salary Structure' : 'Add Salary Structure'}
        open={isStructureModalVisible}
        onCancel={() => {
          setIsStructureModalVisible(false);
          structureForm.resetFields();
          setEditingStructure(null);
        }}
        footer={null}
        width={800}
      >
        <Form
          form={structureForm}
          layout="vertical"
          onFinish={handleSaveSalaryStructure}
          initialValues={{
            currency: 'HKD',
            contribType: 'MPF',
            employeeContribRate: 5,
            employerContribRate: 5,
            contribCapped: true,
            contribCapAmount: 1500,
            contribMinIncome: 7100,
            contribMaxIncome: 30000,
            isActive: true
          }}
        >
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="isActive" label="Status">
                <Select>
                  <Option value={true}>Active</Option>
                  <Option value={false}>Inactive</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Basic Information</Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="name" label="Structure Name" rules={[{ required: true, message: 'Please enter structure name' }]}>
                <Input placeholder="e.g., Software Engineer L4" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="currency" label="Currency">
                <Select>
                  <Option value="HKD">HKD - Hong Kong Dollar</Option>
                  <Option value="USD">USD - US Dollar</Option>
                  <Option value="SGD">SGD - Singapore Dollar</Option>
                  <Option value="CNY">CNY - Chinese Yuan</Option>
                  <Option value="GBP">GBP - British Pound</Option>
                  <Option value="EUR">EUR - Euro</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="baseSalary" label="Base Salary" rules={[{ required: true }]}>
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value!.replace(/,/g, '')}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="description" label="Description">
                <Input.TextArea placeholder="Optional description" rows={2} />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Allowances</Divider>
          <Card size="small" style={{ marginBottom: 16 }}>
            <Table
              dataSource={structureAllowances}
              pagination={false}
              size="small"
              rowKey="id"
              columns={[
                {
                  title: 'Allowance Name',
                  dataIndex: 'name',
                  key: 'name',
                  width: '50%',
                  render: (_, record) => (
                    <Input
                      value={record.name}
                      onChange={(e) => updateStructureAllowance(record.id, 'name', e.target.value)}
                      placeholder="e.g., Housing Allowance"
                    />
                  )
                },
                {
                  title: 'Amount',
                  dataIndex: 'amount',
                  key: 'amount',
                  width: '35%',
                  render: (_, record) => (
                    <InputNumber
                      value={record.amount}
                      onChange={(value) => updateStructureAllowance(record.id, 'amount', value || 0)}
                      style={{ width: '100%' }}
                      min={0}
                      formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={value => value!.replace(/\$\s?|(,*)/g, '')}
                    />
                  )
                },
                {
                  title: '',
                  key: 'action',
                  width: '15%',
                  render: (_, record) => (
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => removeStructureAllowance(record.id)}
                      disabled={structureAllowances.length === 1}
                    />
                  )
                }
              ]}
              footer={() => (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Button type="dashed" onClick={addStructureAllowance} icon={<PlusOutlined />}>
                    Add Allowance
                  </Button>
                  <Text strong>Total Allowances: ${structureTotalAllowances.toLocaleString()}</Text>
                </div>
              )}
            />
          </Card>

          <Divider orientation="left">Statutory Contributions (MPF/CPF/401K)</Divider>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="contribType" label="Contribution Type">
                <Select
                  onChange={(value: string) => {
                    // Auto-fill contribution settings based on selected type
                    const contribDefaults: Record<string, { employeeRate: number; employerRate: number; capped: boolean; capAmount: number; minIncome: number; maxIncome: number }> = {
                      MPF: { employeeRate: 5, employerRate: 5, capped: true, capAmount: 1500, minIncome: 7100, maxIncome: 30000 },
                      CPF: { employeeRate: 20, employerRate: 17, capped: true, capAmount: 6800, minIncome: 500, maxIncome: 102000 },
                      '401K': { employeeRate: 6, employerRate: 4, capped: true, capAmount: 1875, minIncome: 0, maxIncome: 0 },
                      PENSION: { employeeRate: 5, employerRate: 3, capped: false, capAmount: 0, minIncome: 0, maxIncome: 0 },
                      OTHER: { employeeRate: 0, employerRate: 0, capped: false, capAmount: 0, minIncome: 0, maxIncome: 0 }
                    };
                    const defaults = contribDefaults[value];
                    if (defaults) {
                      structureForm.setFieldsValue({
                        employeeContribRate: defaults.employeeRate,
                        employerContribRate: defaults.employerRate,
                        contribCapped: defaults.capped,
                        contribCapAmount: defaults.capAmount,
                        contribMinIncome: defaults.minIncome,
                        contribMaxIncome: defaults.maxIncome
                      });
                    }
                  }}
                >
                  <Option value="MPF">MPF (Hong Kong)</Option>
                  <Option value="CPF">CPF (Singapore)</Option>
                  <Option value="401K">401K (US)</Option>
                  <Option value="PENSION">Pension (UK/EU)</Option>
                  <Option value="OTHER">Other</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="employeeContribRate" label="Employee Rate (%)">
                <InputNumber style={{ width: '100%' }} min={0} max={100} precision={2} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="employerContribRate" label="Employer Rate (%)">
                <InputNumber style={{ width: '100%' }} min={0} max={100} precision={2} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="contribCapped" label="Cap Contribution?">
                <Select>
                  <Option value={true}>Yes</Option>
                  <Option value={false}>No</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="contribCapAmount" label="Cap Amount (Monthly)">
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value!.replace(/,/g, '')}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="contribMinIncome" label="Min Income (No Contrib)">
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value!.replace(/,/g, '')}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="contribMaxIncome" label="Max Relevant Income">
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value!.replace(/,/g, '')}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Tax & Deductions</Divider>
          <Card size="small" style={{ marginBottom: 16 }}>
            <Table
              dataSource={structureDeductions}
              pagination={false}
              size="small"
              rowKey="id"
              columns={[
                {
                  title: 'Deduction Name',
                  dataIndex: 'name',
                  key: 'name',
                  width: '35%',
                  render: (_, record) => (
                    <Input
                      value={record.name}
                      onChange={(e) => updateStructureDeduction(record.id, 'name', e.target.value)}
                      placeholder="e.g., Tax, Insurance"
                    />
                  )
                },
                {
                  title: 'Type',
                  dataIndex: 'type',
                  key: 'type',
                  width: '25%',
                  render: (_, record) => (
                    <Select
                      value={record.type}
                      onChange={(value) => updateStructureDeduction(record.id, 'type', value)}
                      style={{ width: '100%' }}
                    >
                      <Option value="percentage">Percentage (%)</Option>
                      <Option value="fixed">Fixed Amount</Option>
                    </Select>
                  )
                },
                {
                  title: 'Value',
                  dataIndex: 'value',
                  key: 'value',
                  width: '25%',
                  render: (_, record) => (
                    <InputNumber
                      value={record.value}
                      onChange={(value) => updateStructureDeduction(record.id, 'value', value || 0)}
                      style={{ width: '100%' }}
                      min={0}
                      max={record.type === 'percentage' ? 100 : undefined}
                      precision={2}
                      addonAfter={record.type === 'percentage' ? '%' : '$'}
                    />
                  )
                },
                {
                  title: '',
                  key: 'action',
                  width: '15%',
                  render: (_, record) => (
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => removeStructureDeduction(record.id)}
                      disabled={structureDeductions.length === 1}
                    />
                  )
                }
              ]}
              footer={() => (
                <Button type="dashed" onClick={addStructureDeduction} icon={<PlusOutlined />}>
                  Add Deduction
                </Button>
              )}
            />
          </Card>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingStructure ? 'Update Structure' : 'Create Structure'}
              </Button>
              <Button onClick={() => {
                resetStructureForm();
                setIsStructureModalVisible(false);
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PayrollManagement; 